"use client";

import { useAuth } from "@/contexts/AuthContext";
import { useChatInbox } from "@/hooks/useChatInbox";
import { useUserMatchChats } from "@/hooks/useUserMatchChats";
import { useUserTeams } from "@/hooks/useUserTeams";
import { useUserTournaments } from "@/hooks/useUserTournaments";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { UnifiedChatDropdown } from "./UnifiedChatDropdown";

/**
 * Unified header chat bell. Combines match, team, and tournament chats into
 * a single icon + grouped dropdown — replaces the previous two-bell setup.
 *
 * Reads from `chatInboxes/{userId}/entries` (per-user denormalized inbox
 * maintained by Cloud Function fan-out triggers), so total unread reflects
 * every chat surface from a single onSnapshot.
 */
export function UnifiedChatBell() {
  const { user } = useAuth();
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { groups, totalUnread, totalUnreadLabel, isLoading } = useChatInbox(
    user?.id,
  );

  // Pull the user's tournaments + teams so the dropdown can also surface
  // chats the user *can access* but hasn't received messages in yet — the
  // inbox alone only contains chats with prior fan-out activity, so a brand
  // new participant would otherwise see "no chats" despite being on a team.
  const { data: userTournaments = [] } = useUserTournaments(
    user?.tournamentIds,
  );
  const { data: userTeams = [] } = useUserTeams();
  // Only listen for match chats while the dropdown is open — the chat inbox
  // alone covers chats with any prior activity, and this listener is the
  // safety net for brand-new chats with no inbox entry yet. No reason to
  // keep it open on every page for every signed-in user.
  const { data: userMatchChats = [] } = useUserMatchChats(user?.id, {
    enabled: isOpen,
  });

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking into a chat thread that opened above us.
      if (target.closest("[data-chat-window]")) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  return (
    <div ref={containerRef}>
      <button
        onClick={toggle}
        className="relative flex items-center justify-center w-10 h-10 border border-[var(--border-subtle)] bg-transparent text-secondary rounded-lg cursor-pointer transition-[background-color,border-color,color,box-shadow] hover:bg-[rgba(184,134,11,0.15)] hover:border-[var(--border-gold)] hover:text-[var(--gold)] hover:shadow-lg hover:shadow-[var(--gold)]/20"
        title={t("header.chat.title")}
        aria-label={t("header.chat.title")}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <Icon
          icon="solar:chat-round-dots-bold"
          className="text-xl"
          aria-hidden="true"
        />
        {totalUnread > 0 && (
          <span className="absolute -top-3 -right-3 inline-flex items-center justify-center min-w-[24px] h-[24px] px-1 text-sm font-bold leading-none text-white bg-gradient-to-br from-red-500 to-red-600 rounded-full border-2 border-[var(--bg-primary)] shadow-lg shadow-red-500/50 animate-pulse tabular-nums">
            {totalUnreadLabel}
          </span>
        )}
      </button>

      {isOpen && (
        <UnifiedChatDropdown
          groups={groups}
          userTournaments={userTournaments}
          userTeams={userTeams}
          userMatchChats={userMatchChats}
          isLoading={isLoading}
          onClose={close}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}
